import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Send, Phone, CheckCircle, Clock, ArrowLeft, AlertCircle, User } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerSupport = () => {
  const [step, setStep] = useState('form'); // form, success, tickets
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [isSubscriber, setIsSubscriber] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subscriber_name: '',
    mobile: '',
    subject: '',
    message: '',
    priority: 'medium'
  });

  // Auto-lookup subscriber by mobile number
  const lookupSubscriber = useCallback(async (mobile) => {
    if (!mobile || mobile.length < 10) return;
    
    setLookingUp(true);
    try {
      const response = await axios.get(`${API}/customer/lookup/${encodeURIComponent(mobile)}`);
      if (response.data.is_subscriber) {
        setIsSubscriber(true);
        setFormData(prev => ({
          ...prev,
          name: response.data.name || prev.name,
          subscriber_name: response.data.name || prev.subscriber_name
        }));
        toast.success(`Welcome back, ${response.data.name}!`);
      } else {
        setIsSubscriber(false);
      }
    } catch (error) {
      setIsSubscriber(false);
    }
    setLookingUp(false);
  }, []);

  // Debounce mobile lookup
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.mobile && formData.mobile.length >= 10) {
        lookupSubscriber(formData.mobile);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.mobile, lookupSubscriber]);

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.subscriber_name.trim() || !formData.mobile.trim() || !formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/customer/submit-ticket-public`, {
        name: formData.name,
        subscriber_name: formData.subscriber_name,
        mobile: formData.mobile,
        subject: formData.subject,
        message: formData.message,
        priority: formData.priority
      });
      setStep('success');
      toast.success('Your concern has been submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ticket. Please try again.');
    }
    setLoading(false);
  };

  const handleViewTickets = async () => {
    if (!formData.mobile.trim()) {
      toast.error('Please enter your mobile number first');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customer/my-tickets/${encodeURIComponent(formData.mobile)}`);
      setTickets(response.data.tickets);
      setStep('tickets');
    } catch (error) {
      toast.error('No tickets found for this mobile number');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setStep('form');
    setFormData({ name: '', subscriber_name: '', mobile: '', subject: '', message: '', priority: 'medium' });
    setTickets([]);
    setIsSubscriber(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'resolved':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'closed':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg border-2 border-blue-400">
              <span className="text-white font-bold text-2xl tracking-tight">Mipay</span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Submit Ticket Form */}
          {step === 'form' && (
            <>
              <div className="text-center mb-6">
                <MessageSquare className="mx-auto text-blue-600 mb-3" size={40} />
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Customer Support</h1>
                <p className="text-slate-600">Submit your concern and we'll get back to you</p>
              </div>

              <form onSubmit={handleSubmitTicket} className="space-y-4">
                <div>
                  <Label htmlFor="name">Your Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10"
                      placeholder="Juan Dela Cruz"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="mobile">Mobile Number *</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      className="pl-10"
                      placeholder="09123456789"
                      required
                    />
                    {lookingUp && (
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 text-xs">
                        Checking...
                      </span>
                    )}
                  </div>
                  {isSubscriber === true && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <CheckCircle size={12} /> Subscriber recognized
                    </p>
                  )}
                  {isSubscriber === false && formData.mobile.length >= 10 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Not a registered subscriber (you can still submit a concern)
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="subscriber_name">Subscriber Name *</Label>
                  <Input
                    id="subscriber_name"
                    type="text"
                    value={formData.subscriber_name}
                    onChange={(e) => setFormData({ ...formData, subscriber_name: e.target.value })}
                    placeholder="Name of the account holder"
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Enter the name registered with the subscription
                  </p>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="e.g., No Internet Connection"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Your Concern *</Label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Please describe your concern in detail..."
                    required
                    rows="4"
                    className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - General inquiry</SelectItem>
                      <SelectItem value="medium">Medium - Service issue</SelectItem>
                      <SelectItem value="high">High - No internet / Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  <Send size={18} className="mr-2" />
                  {loading ? 'Submitting...' : 'Submit Concern'}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-slate-200 text-center">
                <p className="text-sm text-slate-500 mb-2">Already submitted a concern?</p>
                <button
                  onClick={handleViewTickets}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View My Tickets
                </button>
              </div>
            </>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-emerald-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
              <p className="text-slate-600 mb-6">
                Your concern has been submitted successfully. We will contact you shortly at <strong>{formData.mobile}</strong>.
              </p>
              <Button onClick={resetForm} variant="outline">
                Submit Another Concern
              </Button>
            </div>
          )}

          {/* View Tickets */}
          {step === 'tickets' && (
            <>
              <div className="mb-6">
                <button
                  onClick={() => setStep('form')}
                  className="flex items-center text-slate-600 hover:text-slate-900 mb-4"
                >
                  <ArrowLeft size={18} className="mr-1" />
                  Back
                </button>
                <h2 className="text-xl font-bold text-slate-900">My Tickets</h2>
                <p className="text-slate-600 text-sm">Mobile: {formData.mobile}</p>
              </div>

              {tickets.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {tickets.map((ticket) => (
                    <div key={ticket.id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-slate-900">{ticket.subject}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{ticket.message}</p>
                      <div className="flex justify-between items-center text-xs text-slate-500">
                        <span className={`px-2 py-0.5 rounded-full border ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                        <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                      </div>
                      {ticket.response && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">Response:</p>
                          <p className="text-sm text-slate-700">{ticket.response}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle className="mx-auto mb-2" size={32} />
                  <p>No tickets found</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-slate-400 text-sm">WAGA Internet Services © 2025</p>
        </div>
      </div>
    </div>
  );
};

export default CustomerSupport;
